<template>
  <v-card flat class="mt-2">
    <!-- Non-blocking: logging hours locally is legitimate even when they cannot
         be forwarded. The point is that the user finds out now rather than from
         a payroll discrepancy later. Mirrors the web's Add Hours warning. -->
    <v-alert v-if="billingLinkWarning" type="warning" dense text class="mb-2">
      {{ billingLinkWarning }}
    </v-alert>
    <form-data :references.sync="formReferences" :model="hourObj" ref="normalHourReference">
      <template slot="section-top">
      <!-- <Slider :timeline="timeline"></Slider> -->
        <!-- <v-range-slider ref="hoursRangeSlider" :tick-labels="sliderOptions.sliderLabels" :value="sliderOptions.value" :min="sliderOptions.min" :max="sliderOptions.max" readonly dense tick-size="3"></v-range-slider> -->
      </template>
      <!-- 4993 A4: an existing flex engine line shows its overtime rate and flex
           choice read-only; a pending choice can be made here (D-FLEX2-4). -->
      <template slot="extraDetails">
        <v-row v-if="flexEngineLine" class="mx-0 px-1 pb-1" dense>
          <v-col cols="6" sm="4" md="3">
            <v-text-field outlined dense hide-details="auto" readonly :label="$t('message.flex.overtime')" :value="overtimeRateLabel"></v-text-field>
          </v-col>
          <v-col cols="6" sm="4" md="3">
            <v-text-field outlined dense hide-details="auto" readonly :label="$t('message.flex.flexChoice')" :value="flexChoiceLabel"></v-text-field>
          </v-col>
          <v-col v-if="canDecideFlexChoice" cols="12" sm="4" md="3">
            <v-btn color="primary" outlined block id="flexDecideNow" @click="decideFlexChoiceNow">{{ $t('message.flex.decideNow') }}</v-btn>
          </v-col>
        </v-row>
      </template>
    </form-data>
    <flex-choice-dialog v-model="showFlexChoiceDialog" :summary="flexChoiceSummary" @chosen="onFlexChoiceChosen" @closed="onFlexChoiceClosed"></flex-choice-dialog>
  </v-card>
</template>
<script>

import FormTemplate from '@/components/FormTemplate'
import { selectableOptions, activityLabel, workTypeLabel } from '@/utils/activeRecords'
import { anyBillingIntegrationEnabled, billingProviderLabel } from '@/utils/billingProvider'
import { mapGetters } from 'vuex'
import { isFlexEntry } from '@/utils/flexMode.mjs'
import { needsFlexPrompt, summarizePunchOut, summarizeLine } from '@/utils/flexPrompt.mjs'
import FlexChoiceDialog from '@/components/FlexChoiceDialog'
import moment from 'moment'
import CONSTANTS from '@/assets/json/constants.json'
import projectMixin from '@/mixins/projectMixin'
// import { hostAppApi } from '../../../plugins/axios_settings'

export default {
  mixins: [projectMixin],
  props: ['hourObj', 'listItems', 'isWeekLocked', 'backLink'], /* 'sliderOptions', */
  data () {
    return {
      HOURS: 'hours',
      userObj: this.$store.state.auth.userDetails,
      listOfbreaks: [{ val: 0 }, { val: 15 }, { val: 30 }, { val: 45 }, { val: 60 }],
      listOfActivities: [],
      isValidTime: true,
      canWatchShiftId: false,
      // True while the picked shift came from the auto-suggest path. Reset to
      // false the moment the user clicks the shift picker, so date-change
      // re-suggestion never overrides their explicit choice.
      shiftAutoSuggested: false,
      EditAmountValue: null,
      time: null,
      integrations: JSON.parse(localStorage.getItem('Integrations') || '{}'),
      // 4993 A3/A4: flex prompt state; afterFlexDialog runs once the dialog closes
      showFlexChoiceDialog: false,
      flexChoiceSummary: null,
      afterFlexDialog: null
    }
  },
  components: {
    'form-data': FormTemplate,
    'flex-choice-dialog': FlexChoiceDialog
  },
  computed: {
    /*
     * "This entry will not be transferred to <integration>" — shown when an
     * integration is on and the chosen activity carries no external id, so the
     * hours would be silently dropped at sync time.
     *
     * The integration is named from the server's payload. It used to be
     * hardcoded as Tripletex in the string itself, so a PowerOffice tenant was
     * told to go and check a system they do not use.
     *
     * Only the activity is checked here: it is the one of the three links the
     * person filling this form actually chooses.
     */
    billingLinkWarning () {
      if (!this.anyIntegrationEnabled) return null

      const activity = (this.listOfActivities || []).find(a => a.task_id === this.hourObj.task_id)
      if (!activity || activity.billing_mapped !== false) return null

      const warning = this.$t('message.timer.not_linked_warning', {
        integration: billingProviderLabel(this.integrations)
      })

      return `${warning} (${this.$t('message.timer.activity')})`
    },
    anyIntegrationEnabled () {
      return anyBillingIntegrationEnabled(this.integrations)
    },
    /*
     * Case 5142 R6: filter on the new-entry path, resolve on the display path.
     * `selectableOptions` keeps the currently-referenced record visible even
     * when it has been deactivated, so an existing entry never renders blank.
     */
    activityOptions () {
      return selectableOptions(
        this.listOfActivities, this.hourObj.task_id, 'task_id',
        activityLabel, this.$t('message.timer.inactive_suffix')
      )
    },
    workTypeOptions () {
      const all = (this.listItems?.workTypes || []).filter(x => x.is_billable && x.additional_work === false)
      return selectableOptions(all, this.hourObj.work_type_id, 'id', workTypeLabel, this.$t('message.timer.inactive_suffix'))
    },
    additionalWorkTypeOptions () {
      const all = (this.listItems?.workTypes || []).filter(x => x.is_billable && x.additional_work === true)
      return selectableOptions(all, this.hourObj.additional_work_type_id, 'id', workTypeLabel, this.$t('message.timer.inactive_suffix'))
    },
    ...mapGetters(['formType', 'generalSettings', 'getTimerObj', 'getuserDetails', 'getHostRefApi', 'flexHoursEnabled']),
    // 4993 / D-FLEX-4: an existing entry follows its own API-reported mode; a
    // new entry follows the tenant flag.
    flexEntry () {
      return isFlexEntry(this.hourObj, this.flexHoursEnabled)
    },
    // 4993 A4: an engine-split line carries overtime_rate (50/100); legacy rows never do.
    flexEngineLine () {
      const rate = this.hourObj ? this.hourObj.overtime_rate : null
      return Boolean(this.hourObj && this.hourObj.id && this.flexEntry && rate !== null && rate !== undefined && rate !== '')
    },
    overtimeRateLabel () {
      return this.flexEngineLine ? `${this.hourObj.overtime_rate} %` : ''
    },
    flexChoiceLabel () {
      const choice = this.hourObj ? this.hourObj.flex_choice : null
      if (!choice) return ''
      const labels = {
        pending: this.$t('message.flex.choice.pending'),
        flex: this.$t('message.flex.choice.flex'),
        payout: this.$t('message.flex.choice.payout')
      }
      return labels[choice] || choice
    },
    canDecideFlexChoice () {
      return this.flexEngineLine && this.hourObj.flex_choice === 'pending' && !this.isReadOnly && !this.isWeekLocked
    },
    formReferences () {
      return {
        properties: [{
          model: 'date',
          type: this.formType.DATEPICKER,
          rules: this.$_requiredValidation,
          picker: 'dateStringPicker',
          menu: 'dateStringMenu',
          color: 'primary lighten-1',
          label: this.$t('message.common.date'),
          disabled: this.hourObj.id ? this.isWeekLocked || this.isReadOnly : false,
          class: 'xs6 sm4 md3 lg3'
        }, {
          model: 'total',
          type: this.formType.TEXT,
          rules: [],
          label: this.$t('message.common.total'),
          class: 'xs6 sm4 md3 lg3',
          disabled: true
        }, {
          model: 'project_id',
          type: this.formType.AUTO_COMPLETE,
          rules: this.$_requiredValidation,
          items: this.listItems.listOfProjects,
          select_text: (item) => this.setProjectname(item),
          select_value: 'id',
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.layout.projects'),
          class: 'xs12 sm4 md3 lg3',
          is_show: this.$store.state.common.isProjectExcluded ? this.setDefaultProject() : true
        }, {
          model: 'shift_id',
          type: this.formType.SELECT,
          rules: this.$_requiredValidation,
          items: this.listItems.listOfShifts,
          select_text: 'displayName',
          select_value: 'id',
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.timer.shift'),
          class: ' xs12 sm4 md3 lg3',
          click: this.setWatchForShiftId
        }, {
          model: 'amount',
          type: this.formType.TEXT,
          rules: this.hourObj.amount ? this.$_HourValidation : this.$_requiredValidation,
          label: this.$t('message.timer.hours'),
          class: 'xs6 sm4 md3 lg3',
          disabled: this.isWeekLocked || this.isReadOnly,
          is_show: this.generalSettings.hour_enter_style === 2 && !this.generalSettings.use_shift
        }, {
          model: 'start_time',
          type: this.formType.TIMEPICKER,
          rules: this.$_requiredValidation,
          menu: 'startTimeMenu',
          label: this.$t('message.timer.from'),
          disabled: this.isWeekLocked || this.isReadOnly,
          class: 'xs6 sm4 md3 lg3',
          is_show: this.generalSettings.hour_enter_style === 1 || this.generalSettings.use_shift
        }, {
          model: 'end_time',
          min: 'start_time',
          type: this.formType.TIMEPICKER,
          rules: this.$_requiredValidation,
          menu: 'endTimeMenu',
          label: this.$t('message.timer.till'),
          disabled: this.isWeekLocked || this.isReadOnly,
          class: 'xs6 sm4 md3 lg3',
          is_show: this.generalSettings.hour_enter_style === 1 || this.generalSettings.use_shift
        }, {
          model: 'break',
          type: this.formType.SELECT,
          rules: [], // this.$_requiredValidation,
          items: this.listOfbreaks,
          select_text: (item) => item.val + ` ${this.$t('message.timer.minutes')}`,
          select_value: 'val',
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.timer.pause'),
          class: `sm4 md3 lg3 ${this.generalSettings.hour_enter_style === 1 || this.generalSettings.use_shift ? 'xs12' : 'xs6'}`
        }, {
          model: 'task_id',
          type: this.formType.SELECT,
          rules: this.$store.state.common.isProjectExcluded ? [] : this.$_requiredValidation,
          // Active activities only for a new entry; the one this entry already
          // references stays pinned and labelled "(inaktiv)" (case 5142, R6).
          items: this.activityOptions,
          select_text: 'display_name',
          select_value: 'task_id',
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.timer.activity'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'work_type_id',
          type: this.formType.SELECT,
          rules: this.generalSettings.isWorkTypeRequired ? this.$_requiredValidation : [],
          items: this.workTypeOptions,
          select_text: 'display_name',
          select_value: 'id',
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.common.worktype'),
          class: 'xs12 sm4 md3 lg3',
          // 4993 A4: flex mode hides the field (the engine assigns the work type per
          // line); the id stays in the payload so the API shape does not change.
          is_show: !this.flexEntry
        }, {
          model: 'additional_work_type_id',
          type: this.formType.SELECT,
          rules: [],
          items: this.additionalWorkTypeOptions,
          select_text: 'display_name',
          select_value: 'id',
          disabled: this.isWeekLocked || this.isReadOnly,
          is_show: this.integrations.tripletex === 1,
          label: this.$t('message.timer.additionalWorktype'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'additional_hours',
          type: this.formType.TEXT,
          rules: [],
          label: this.$t('message.timer.additional_hours'),
          class: 'xs12 sm4 md3 lg3',
          disabled: this.isWeekLocked || this.isReadOnly || !this.hourObj.additional_work_type_id,
          is_show: this.integrations.tripletex === 1
        }, {
          model: 'location',
          type: this.formType.TEXT,
          rules: [],
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.timer.place'),
          class: 'xs12 sm4 md3 lg3'
        }, {
          model: 'comments',
          type: this.formType.TEXTAREA,
          rules: this.$_requiredValidation,
          disabled: this.isWeekLocked || this.isReadOnly,
          label: this.$t('message.common.comments'),
          class: 'xs12 sm4 md6 lg6',
          rows: 3
        }, {
          model: 'not_approved_comment',
          type: this.formType.TEXTAREA,
          rules: [],
          // eslint-disable-next-line
          is_show: this.hourObj.state === CONSTANTS.NOT_APPROVED,
          readonly: true,
          label: this.$t('message.timer.notApproved'),
          class: 'xs12 sm4 md6 lg6 custom_opacity_reduced',
          rows: 3
        }],
        buttons: [{
          name: 'action_handler',
          color: 'success',
          label: this.hourObj.id ? this.$t('message.common.update') : this.$t('message.common.save'),
          click: this.saveUpdateHandler,
          disabled: this.isWeekLocked || this.isReadOnly,
          loading: this.loading,
          is_show: true
        }, {
          name: 'back',
          color: 'info',
          click: () => this.$router.go(-1), // this.$router.push(this.backLink),
          label: this.$t('message.login.back'),
          to: this.backLink,
          is_show: true
        }, {
          name: 'delete',
          color: 'error',
          click: this.deleteHandler,
          label: this.$t('message.common.delete'),
          is_show: this.hourObj.id && !this.isWeekLocked && !this.isReadOnly
        }]
      }
    },
    isReadOnly () {
      return this.hourObj.state === CONSTANTS.DELIVERED || this.hourObj.state === CONSTANTS.APPROVED
    }
  },
  async mounted () {
    // Same guard as the Non Billable tab: a work type left over from the other
    // tab must not stay selected here.
    this.applyDefaultWorkType()
    const lastShiftUsed = localStorage.getItem(`lastShiftUsed_${this.getuserDetails.id}`)
    this.integrations = JSON.parse(localStorage.getItem('Integrations') || '{}')
    const shifts = this.listItems?.listOfShifts || []
    const result = shifts.find(x => x.default)
    if (this.hourObj.shift_id !== '') {
      // Keep same Shift ID
    } else {
      // Server-side scheduled-shift takes priority over local heuristics so
      // pickers stay in sync across devices and pick up backend "last used by
      // this user" without relying on localStorage.
      // 4993: the server suggestion is flex-only; legacy keeps the local heuristics.
      let scheduled = null
      if (this.flexHoursEnabled) {
        scheduled = await this.$store.dispatch('loadScheduledShift', {
          userId: this.getuserDetails.id,
          date: this.hourObj.date || moment().format('DD.MM.YYYY')
        })
      }
      const scheduledShift = scheduled ? shifts.find(x => x.id === scheduled.shift_id) : null
      if (scheduledShift) {
        this.hourObj.shift_id = scheduledShift.id
        this.shiftAutoSuggested = true
      } else if (lastShiftUsed) {
        this.hourObj.shift_id = lastShiftUsed
        this.shiftAutoSuggested = true
      } else if (result) {
        this.hourObj.shift_id = result.id
        this.shiftAutoSuggested = true
      } else if (shifts.length > 0) {
        this.hourObj.shift_id = shifts[0].id
        this.shiftAutoSuggested = true
      }
    }
    // Set break value based on selected shift
    const selectedShift = shifts.find(x => x.id === this.hourObj.shift_id)
    if (selectedShift) {
      const breakValue = Number(selectedShift.break)
      if (!this.listOfbreaks.some(b => b.val === breakValue)) {
        this.listOfbreaks = [...this.listOfbreaks, { val: breakValue }]
      }
      this.hourObj.break = breakValue
    }
    setTimeout(() => {
      this.EditAmountValue = this.hourObj ? this.hourObj.amount : null
    }, 100)
    if (this.hourObj.date) this.callDateChangeMethods()
    if (this.hourObj.project_id) this.getActivitiesForProject(this.hourObj.project_id)
    this.$eventBus.$on('confirmedToProceed', (data, saveData) => {
      if (data === 'hours') {
        this.$store.commit('hideConfirmationDialog')
        this.proceedToSaveUpdateHandler(saveData.url, saveData.model)
      }
    })
  },
  methods: {
    /**
     * Keep a NEW entry's work type inside this tab's list (billable, not
     * additional work). The two tabs share one hourObj, so a non-billable id
     * picked on Non Billable would otherwise stay selected here and show as its
     * raw id. Replaced by the default billable type, or cleared when there is
     * none. Saved entries keep whatever they hold.
     */
    applyDefaultWorkType () {
      if (this.hourObj.id) return
      const list = (this.listItems && this.listItems.workTypes) || []
      if (!list.length) return
      const fits = list.some(x => x.id === this.hourObj.work_type_id && x.is_billable && x.additional_work === false)
      if (fits) return
      const def = list.find(x => x.is_default && x.is_billable)
      this.$set(this.hourObj, 'work_type_id', def ? def.id : null)
    },
    getCurrentDayHours () {
      const startDate = this.$formatter.formatDate(this.hourObj.date, 'DD.MM.YYYY', 'MM.DD.YYYY')
      const endDate = this.$formatter.formatDate(this.hourObj.date, 'DD.MM.YYYY', 'MM.DD.YYYY 23:59:59')
      const model = { start: startDate, end: endDate, users: [this.userObj ? this.userObj.id : 0] }
      model.showAllProjects = true
      this.$api.execute('post', 'hours/filter', model).then((response) => {
        if (response.data && response.data.length > 0) {
          if (this.hourObj.id) response.data = response.data.filter(x => x.id !== this.hourObj.id)
          var dates = response.data.map((hour) => {
            return moment(hour.end_date, 'DD.MM.YYYY HH:mm:ss')
          })
          this.generalSettings.standard_start_time = moment.max(dates).format('HH:mm:ss')
        } else this.generalSettings.standard_start_time = this.$store.state.common.standardStartTime
      }).catch(() => {})
    },
    getActivitiesForProject (id) {
      this.$api.execute('get', `projects/${id}/activities?include_inactive=1`)
        .then(response => {
          this.listOfActivities = this.$formatter.cloneVariable(response.data)
        }).catch(() => {})
    },
    saveUpdateHandler () {
      if (this.$refs.normalHourReference.$refs.validateForm.validate()) {
        const model = this.$formatter.cloneVariable(this.hourObj)
        model.state = model.id ? model.state !== CONSTANTS.NOT_APPROVED ? model.state : CONSTANTS.NOT_DELIVERED : CONSTANTS.NOT_DELIVERED
        /* adding conditions from timer  */
        let fieldsCondition = false
        let shiftCondition = false
        if (this.generalSettings.use_shift) {
          shiftCondition = this.hourObj.shift_id !== ''
          fieldsCondition = true
        }
        if (this.generalSettings.hour_enter_style === 1 && !this.generalSettings.use_shift) {
          fieldsCondition = this.hourObj.start_time !== '' && this.hourObj.end_time !== ''
          shiftCondition = true
        } else if (this.generalSettings.hour_enter_style === 2 && !this.generalSettings.use_shift) {
          fieldsCondition = this.hourObj.amount && this.hourObj.amount.toString().replace(',', '.') > 0
          shiftCondition = true
        }
        if (fieldsCondition && shiftCondition) {
          let getStartDate = ''
          let getEndDate = ''
          let runningShiftTime = ''
          let totalrunningShiftTime = ''
          if (this.generalSettings.hour_enter_style === 1 || (this.generalSettings.hour_enter_style === 2 && this.generalSettings.use_shift)) {
            // Detect cross-midnight: either the picked shift has a positive
            // dayOffsetTo, or the user entered end_time before start_time.
            // Both cases mean "end is on a later calendar day". Without this,
            // the backend's `end_date > start_date` validation would reject
            // a 22:00 → 06:00 entry as having end before start.
            const pickedShift = this.listItems.listOfShifts.find(x => x.id === this.hourObj.shift_id)
            const shiftOffsetDays = Number(pickedShift?.dayOffsetTo) || 0
            const endBeforeStart = moment(this.hourObj.end_time, 'HH:mm').isSameOrBefore(moment(this.hourObj.start_time, 'HH:mm'))
            // 4993 / D-FLEX-10: cross-midnight entry dates are a flex rule. A
            // legacy entry keeps the pre-flag same-day payload (the API then
            // rejects end ≤ start exactly as before).
            const endDayOffset = this.flexEntry ? (shiftOffsetDays > 0 ? shiftOffsetDays : (endBeforeStart ? 1 : 0)) : 0
            getStartDate = `${this.hourObj.date} ${this.hourObj.start_time}`
            const endDateForFormat = endDayOffset > 0
              ? moment(this.hourObj.date, 'DD.MM.YYYY').add(endDayOffset, 'days').format('DD.MM.YYYY')
              : this.hourObj.date
            getEndDate = `${endDateForFormat} ${this.hourObj.end_time}`
            const shiftTime = moment(this.hourObj.end_time, 'HH:mm').add(endDayOffset, 'days').diff(moment(this.hourObj.start_time, 'HH:mm'))
            const totalShiftTime = Number(moment.duration(shiftTime).hours()) + '.' + Number(moment.duration(shiftTime).minutes())
            const totalNormalValue = this.hourObj.total.toString().split('(')[0].replace(',', '.').trim()
            const totalAbsenceValue = this.hourObj.total.toString().split('(')[1].split(')')[0].replace(',', '.')
            const runningShift = this.listItems.listOfShifts.find(x => x.id === (this.getTimerObj ? Number(this.getTimerObj.shift_id) : 0))
            if (runningShift) {
              const runningOffset = Number(runningShift.dayOffsetTo) || 0
              runningShiftTime = moment(runningShift.timeTo, 'HH:mm').add(runningOffset, 'days').diff(moment(runningShift.timeFrom, 'HH:mm'))
              totalrunningShiftTime = Number(moment.duration(runningShiftTime).hours()) + '.' + Number(moment.duration(runningShiftTime).minutes())
            }
            if (!this.hourObj.id) {
              (totalrunningShiftTime ? Number(totalrunningShiftTime) : 0) + Number(totalShiftTime) + Number(totalNormalValue) + Number(totalAbsenceValue) > 24 ? this.isValidTime = false : this.isValidTime = true
            } else {
              Number(totalShiftTime) + (Number(totalNormalValue) - Number(this.hourObj.amount.toString().replace(',', '.').trim())) + Number(totalAbsenceValue) > 24 ? this.isValidTime = false : this.isValidTime = true
            }
            // this.isValidTime = true
          } else if (this.generalSettings.hour_enter_style === 2 && !this.generalSettings.use_shift) {
            const startTime = this.generalSettings.standard_start_time
            const calEndTime = moment(startTime, 'HH:mm').add(this.hourObj.amount.toString().replace(',', '.'), 'hours').format('HH:mm:ss')
            getStartDate = `${this.hourObj.date} ${startTime}`
            getEndDate = `${this.hourObj.date} ${calEndTime}`
            const totalAbsenceValue = this.hourObj.total.toString().split('(')[1].split(')')[0].replace(',', '.')
            const totalNormalValue = this.hourObj.total.toString().split('(')[0].replace(',', '.')
            const totalEnteredHours = model.total.toString().split(',')[0].replace(',', '.')
            if (!this.hourObj.id) Number(totalNormalValue) + Number(model.amount) + Number(totalAbsenceValue) > 24 ? this.isValidTime = false : this.isValidTime = true
            else (Number(totalEnteredHours) - Number(totalNormalValue)) + Number(model.amount) > 24 ? this.isValidTime = false : this.isValidTime = true

            // if (!this.hourObj.id) Number(this.hourObj.total.toString().split(',')[0]) + Number(model.amount) + Number(this.hourObj.total.toString().split('(')[1].split(',')[0]) >= 24 ? this.isValidTime = false : this.isValidTime = true
            // else (Number(model.total.toString().split(',')[0]) - Number(this.EditAmountValue.toString().split(',')[0])) + Number(model.amount) + Number(model.total.toString().split('(')[1].split(',')[0]) >= 24 ? this.isValidTime = false : this.isValidTime = true
            // moment.duration(this.generalSettings.standard_end_time).asHours() < moment.duration(calEndTime).asHours()
          }
          const startDate = moment(getStartDate, 'DD.MM.YYYY HH:mm')
          const endDate = moment(getEndDate, 'DD.MM.YYYY HH:mm')
          const duration = moment.duration(endDate.diff(startDate))
          const hours = parseFloat(duration.asHours()) % 60
          model.start_date = startDate.format('MM.DD.YYYY HH:mm')
          model.end_date = endDate.format('MM.DD.YYYY HH:mm')
          if (this.generalSettings.hour_enter_style === 1 || (this.generalSettings.hour_enter_style === 2 && this.generalSettings.use_shift)) {
            model.amount = hours
          } else if (this.generalSettings.hour_enter_style === 2 && !this.generalSettings.use_shift) {
            model.amount = this.hourObj.amount.toString().replace(',', '.')
          }
          model.break = moment.duration(parseFloat(model.break), 'minutes').asHours()
          model.user_id = this.userObj ? this.userObj.id : 0
          model.type = CONSTANTS.WORKING
          const url = this.HOURS
          if (model.amount > 0) {
            if (model.amount > model.break) {
              localStorage.setItem(`lastShiftUsed_${model.user_id}`, model.shift_id)
              if (this.isValidTime) this.proceedToSaveUpdateHandler(url, model)
              else { // confirmAddExceedingHour
                this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.hourExceedNotification' })
              }
            } else {
              this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.shifts.notValidPausedTime' })
            }
          } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.seemsInvalid' })
          // const payload = { title: 'message.common.confirmAddHour', text: 'message.common.sureToAddExceedingHour', module: 'hours', saveData: { url, model } }
          // this.$store.commit('showConfirmationDialog', payload)
          /* if (this.isValidTime) {
          } else // this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.hourExceedNotification' }) */
        }
      } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.validationIssue' })
    },
    proceedToSaveUpdateHandler (url, model) {
      this.loading = true
      this.$api.saveUpdateHandler(url, model)
        .then((response) => {
          this.$root.$emit('snackbar', response.snackbar)
          // 4993 A3: a flex entry that earned overtime gets the flex prompt first and
          // the calendar navigation runs once the dialog closes. saveUpdateHandler
          // resolves the body on 2xx only, so the status is the contract's 201/200.
          if (this.flexEntry && needsFlexPrompt(model.id ? 200 : 201, response)) {
            this.flexChoiceSummary = summarizePunchOut(response)
            this.afterFlexDialog = () => this.$router.push('/calendar')
            this.showFlexChoiceDialog = true
            return
          }
          this.$router.push('/calendar')
        })
        .catch((error) => {
          this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: error.response?.data?.message || 'message.common.errorOccured' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 4993 A4: "Decide now" on an existing pending engine line. The row carries no
    // net_minutes, so it is derived from the form's gross hours and break minutes.
    decideFlexChoiceNow () {
      const amountHours = parseFloat(String(this.hourObj.amount || 0).replace(',', '.')) || 0
      const breakMinutes = Number(this.hourObj.break) || 0
      this.flexChoiceSummary = summarizeLine({
        id: this.hourObj.id,
        line_group: this.hourObj.line_group,
        overtime_rate: this.hourObj.overtime_rate,
        flex_choice: this.hourObj.flex_choice,
        net_minutes: Math.max(0, Math.round(amountHours * 60) - breakMinutes)
      })
      this.afterFlexDialog = null
      this.showFlexChoiceDialog = true
    },
    onFlexChoiceChosen (lines, choice) {
      if (!this.hourObj.id) return
      const mine = Array.isArray(lines) ? lines.find(l => l && l.id === this.hourObj.id) : null
      this.$set(this.hourObj, 'flex_choice', mine && mine.flex_choice ? mine.flex_choice : choice)
    },
    onFlexChoiceClosed () {
      const next = this.afterFlexDialog
      this.afterFlexDialog = null
      if (typeof next === 'function') next()
    },
    callDateChangeMethods () {
      this.getCurrentDayHours()
      this.$eventBus.$emit('getactiveDateHourRecords', this.hourObj.date)
    },
    deleteHandler () {
      const payload = { url: `hours/${this.hourObj.id}`, module: 'hours', isSingle: true }
      this.$store.commit('showDeleteDialog', payload)
    },
    setDefaultProject () {
      const projects = this.listItems.listOfProjects
      const obj = projects && projects.length > 0 ? projects.find(x => x.number === 'TM001') : null
      if (obj) {
        this.hourObj.project_id = obj.id
      }
      return false
    },
    setWatchForShiftId () {
      this.canWatchShiftId = true
      // User has actively engaged the shift picker — stop auto-overriding on
      // future date changes.
      this.shiftAutoSuggested = false
    },
    async refreshShiftSuggestionForDate (date) {
      if (this.hourObj.id) return // editing existing entry — never override
      if (!this.shiftAutoSuggested) return // user has picked their own shift
      if (!this.flexHoursEnabled) return // 4993: the server suggestion is flex-only
      const scheduled = await this.$store.dispatch('loadScheduledShift', {
        userId: this.getuserDetails.id,
        date
      })
      if (!scheduled) return
      const shifts = this.listItems?.listOfShifts || []
      const match = shifts.find(x => x.id === scheduled.shift_id)
      if (match && match.id !== this.hourObj.shift_id) {
        this.hourObj.shift_id = match.id
        const breakValue = Number(match.break)
        if (!this.listOfbreaks.some(b => b.val === breakValue)) {
          this.listOfbreaks = [...this.listOfbreaks, { val: breakValue }]
        }
        this.hourObj.break = breakValue
      }
    }
  },
  watch: {
    'hourObj.dateStringPicker' (val) {
      if (val) {
        this.hourObj.date = this.$formatter.formatDate(val, 'YYYY-MM-DD', 'DD.MM.YYYY')
      }
    },
    'hourObj.date' (val) {
      if (val) {
        this.callDateChangeMethods()
        this.refreshShiftSuggestionForDate(val)
      }
    },
    'hourObj.project_id' (val) {
      if (val) {
        this.getActivitiesForProject(val)
        this.getCurrentDayHours()
      }
    },
    'hourObj.shift_id' (val) {
      if (val && this.canWatchShiftId) {
        const shiftsList = this.listItems.listOfShifts
        if (shiftsList) {
          const result = shiftsList.find(x => x.id === val)
          if (result) {
            if (!this.hourObj.start_time) {
              this.hourObj.start_time = moment(result.timeFrom, 'HH:mm:ss').format('HH:mm')
            }
            this.hourObj.end_time = moment(result.timeTo, 'HH:mm:ss').format('HH:mm')
            if (this.hourObj.start_time === this.hourObj.end_time) {
              this.hourObj.end_time = moment(result.timeTo, 'HH:mm:ss').add(2, 'hours').format('HH:mm')
            }
            this.hourObj.break = result.break
          }
        }
        this.canWatchShiftId = false
      }
    },
    'listItems.workTypes' () {
      this.applyDefaultWorkType()
    }
  },
  beforeDestroy () {
    this.$eventBus.$off('confirmedToProceed')
  }
}
</script>
<style>
</style>
